"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.NotificationsListItem = void 0;
var _components = require("@ori-ui/components");
var _icons = require("@ori-ui/icons");
var _mui = require("@ori-ui/mui");
var _react = require("react");
var _dateUtils = require("../../utils/dateUtils");
var _triggerRemoveNotificationEvent = require("../../utils/triggerRemoveNotificationEvent");
var _triggerSelectNotificationEvent = require("../../utils/triggerSelectNotificationEvent");
var _FormattedMessage = require("../FormattedMessage");
var _NotificationIcon = require("./NotificationIcon");
var _jsxRuntime = require("react/jsx-runtime");
const ANIMATION_TIMEOUT = 500;
const ANIMATION_TIMEOUT_WITH_IMAGE = 800;
const NotificationItem = (0, _mui.styled)(_mui.ListItem)(({
  theme
}) => ({
  padding: 0,
  paddingLeft: theme.spacing(3),
  backgroundColor: '#95E0C926',
  marginTop: 0,
  marginLeft: 1,
  marginRight: 1,
  marginBottom: theme.spacing(2),
  minHeight: theme.spacing(20),
  flexWrap: 'wrap',
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(80)} - 0.125rem)`
  },
  '&:hover': {
    boxShadow: `0px 3px 8px ${theme.palette.grey[200]}`
  }
}));
const BodyPadding = (0, _mui.styled)('div')(({
  theme
}) => ({
  padding: theme.spacing(1, 4, 4, 6)
}));
const TitleTypography = (0, _mui.styled)(_mui.Typography)(({
  theme
}) => ({
  padding: theme.spacing(4, 4, 0, 0),
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  fontWeight: 'bold'
}));
const TitlePadding = (0, _mui.styled)('div')(({
  theme
}) => ({
  padding: theme.spacing(0, 0, 0, 6),
  position: 'relative'
}));
const BodyTypography = (0, _mui.styled)(_mui.Typography)(({
  theme
}) => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 5,
  WebkitBoxOrient: 'vertical',
  maxHeight: theme.spacing(22),
  // Making sure it doesn't overflow in edge-case on iOS
  paddingRight: theme.spacing(5)
}));
const ListText = (0, _mui.styled)(_mui.ListItemText)(({
  theme,
  ownerState: {
    hasImage
  }
}) => ({
  margin: ' 0 2.8125rem 3 0',
  ...(hasImage && {
    maxHeight: theme.spacing(33)
  })
}));
const TimeStamp = (0, _mui.styled)(_mui.Typography)(() => ({
  fontSize: 12,
  textAlign: 'right',
  paddingRight: 15,
  paddingBottom: 15
}));
const SecondaryActionListItem = (0, _mui.styled)(_mui.ListItemSecondaryAction, {
  shouldForwardProp: propName => propName !== 'hasImage'
})(({
  theme,
  hasImage
}) => ({
  display: 'flex',
  flexDirection: 'column',
  right: 0,
  top: 0,
  transform: 'none',
  bottom: theme.spacing(hasImage ? 37 : 0),
  [theme.breakpoints.up('sm')]: {
    bottom: theme.spacing(hasImage ? 41 : 0)
  }
}));
const CustomSpan = (0, _mui.styled)('span')(() => ({
  flex: 1
}));
const DeleteButton = (0, _mui.styled)(_components.IconButton)(({
  theme
}) => ({
  alignSelf: 'flex-end',
  padding: theme.spacing(3)
}));
const CustomCross = (0, _mui.styled)(_icons.Clear)(() => ({
  fontSize: '18px !important'
}));
const Icon = (0, _mui.styled)(_NotificationIcon.NotificationIcon)(({
  theme
}) => ({
  position: 'absolute',
  top: '17px',
  left: theme.spacing(-1)
}));
const StyledLazyImage = (0, _mui.styled)(_components.LazyImage)(({
  theme
}) => ({
  [`& .${_components.lazyImageClasses.image}`]: {
    borderRadius: theme.spacing(2),
    objectFit: 'cover'
  },
  width: '100%',
  height: theme.spacing(35),
  margin: theme.spacing(0, 3, 3, 0),
  [theme.breakpoints.up('sm')]: {
    height: theme.spacing(39)
  }
}));
const NotificationsListItem = ({
  notification,
  handleDelete,
  handleMarkAsViewed,
  index,
  section
}) => {
  const [isShowing, setIsShowing] = (0, _react.useState)(true);
  const [isDeleting, setIsDeleting] = (0, _react.useState)(false);
  const {
    timestamp,
    title,
    body,
    isViewed,
    link,
    iconId,
    image
  } = notification;
  const timeOffsetSeconds = (0, _dateUtils.getDateTimeOffsetSeconds)(new Date(timestamp), (0, _dateUtils.currentDateTime)());
  const bestTimeUnit = (0, _dateUtils.getBestFitTimeUnit)(timeOffsetSeconds);
  const timeOffset = (0, _dateUtils.convertOffset)(timeOffsetSeconds, bestTimeUnit);
  const handleDeleteOnClick = (0, _react.useCallback)(event => {
    event.stopPropagation();
    (0, _triggerRemoveNotificationEvent.triggerRemoveNotificationEvent)(notification);
    setIsDeleting(true);
  }, [notification]);
  const handleDeleteOnExited = (0, _react.useCallback)(() => {
    void handleDelete(notification, index, section);
  }, [handleDelete, notification, index, section]);
  const itemOnClick = (0, _react.useCallback)(() => {
    const {
      notificationId
    } = notification;
    if (!isViewed && notificationId !== undefined) {
      void handleMarkAsViewed(notification);
    }
    if (link) {
      document.location.href = link;
    }
  }, [notification, isViewed, link, handleMarkAsViewed]);
  const handleItemOnClick = (0, _react.useCallback)(() => {
    (0, _triggerSelectNotificationEvent.triggerSelectNotificationEvent)(notification);
    itemOnClick();
  }, [itemOnClick, notification]);
  (0, _react.useEffect)(() => {
    if (isShowing) {
      setIsShowing(false);
    }
  }, [isShowing]);
  const animationTimeout = image ? ANIMATION_TIMEOUT_WITH_IMAGE : ANIMATION_TIMEOUT;
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Collapse, {
    in: !isShowing,
    timeout: animationTimeout,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Collapse, {
      in: !isDeleting,
      timeout: animationTimeout,
      onExited: handleDeleteOnExited,
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(NotificationItem, {
        sx: {
          ...(isViewed && {
            backgroundColor: 'common.white'
          }),
          ...(link && {
            cursor: 'pointer'
          })
        },
        onClick: handleItemOnClick,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(ListText, {
          disableTypography: true,
          primary: /*#__PURE__*/(0, _jsxRuntime.jsxs)(TitlePadding, {
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(Icon, {
              iconId: iconId
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(TitleTypography, {
              variant: "body2",
              color: "textPrimary",
              children: title
            })]
          }),
          secondary: /*#__PURE__*/(0, _jsxRuntime.jsx)(BodyPadding, {
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(BodyTypography, {
              variant: "body1",
              color: "textPrimary",
              children: body
            })
          }),
          ownerState: {
            hasImage: !!image
          }
        }), image ? /*#__PURE__*/(0, _jsxRuntime.jsx)(StyledLazyImage, {
          src: image,
          alt: title
        }) : null, /*#__PURE__*/(0, _jsxRuntime.jsxs)(SecondaryActionListItem, {
          hasImage: !!image,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(DeleteButton, {
            variant: "text",
            onClick: handleDeleteOnClick,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(CustomCross, {})
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(CustomSpan, {}), /*#__PURE__*/(0, _jsxRuntime.jsxs)(TimeStamp, {
            variant: "body2",
            color: "textSecondary",
            fontWeight: "bold",
            children: [timeOffset < 1 ? '' : timeOffset, /*#__PURE__*/(0, _jsxRuntime.jsx)(_FormattedMessage.FormattedMessage, {
              id: bestTimeUnit
            })]
          })]
        })]
      })
    })
  });
};
exports.NotificationsListItem = NotificationsListItem;
